// src/components/ui/NiizSystemDoctor.tsx

import { useState, useEffect } from 'react';
import { 
  Activity, Database,
  Trash2, RefreshCw, Terminal, CheckCircle2,
  ShieldAlert, Bug, Power
} from 'lucide-react';
import { db } from '@/db/db';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { useCashStore } from '@/store/cashStore';
import { useConfigStore } from '@/store/configStore'; // IMPORTADO
import { toast } from 'sonner';
import clsx from 'clsx';

interface Log {
  id: string;
  type: 'ERROR' | 'WARNING' | 'INFO' | 'NETWORK';
  message: string;
  timestamp: string;
  stack?: string;
}

// HELPER SEGURO PARA GENERAR UUID
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const NiizSystemDoctor = () => {
  // Configuración Global para verificar modo desarrollador
  const { settings } = useConfigStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<Log[]>([]);
  const [dbStatus, setDbStatus] = useState<'OK' | 'ERROR' | 'CHECKING'>('CHECKING');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [tab, setTab] = useState<'LOGS' | 'TOOLS'>('LOGS');
  
  // Stores
  const auth = useAuthStore();
  const cart = useCartStore();
  const cash = useCashStore();

  // ─────────────────────────────────────────────────────────────
  // 0. BLOQUEO DE SEGURIDAD: SI NO ES DEV, NO RENDERIZA NADA
  // ─────────────────────────────────────────────────────────────
  if (!(settings as any)?.developer_mode) {
    return null;
  }

  // ─────────────────────────────────────────────────────────────
  // 1. ACTUALIZACIÓN EN TIEMPO REAL
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      cash.checkShiftStatus();
      checkDbHealth();
    }
  }, [isOpen]);

  // ─────────────────────────────────────────────────────────────
  // 2. INTERCEPTOR DE ERRORES (CONSOLE WRAPPER)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      const message = args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
      addLog('ERROR', message);
      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      const message = args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
      if (!message.includes('React Router Future Flag')) {
        addLog('WARNING', message);
      }
      originalWarn.apply(console, args);
    };

    const handleOnline = () => { setIsOnline(true); addLog('INFO', 'Conexión a Internet restaurada'); };
    const handleOffline = () => { setIsOnline(false); addLog('ERROR', 'Conexión a Internet perdida'); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    window.onerror = (msg, url, line, _col, error) => {
      addLog('ERROR', `Global: ${msg} (${url}:${line})`, error?.stack);
    };

    window.onunhandledrejection = (event) => {
      addLog('ERROR', `Promesa no manejada: ${event.reason}`);
    };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const addLog = (type: Log['type'], message: string, stack?: string) => {
    const newLog: Log = {
      id: generateUUID(),
      type,
      message: message.substring(0, 300),
      timestamp: new Date().toLocaleTimeString(),
    };
    if (stack) newLog.stack = stack;
    setLogs(prev => [newLog, ...prev].slice(0, 100));
  };

  const checkDbHealth = async () => {
    setDbStatus('CHECKING');
    try {
      if (db.isOpen()) {
        await db.settings.count();
        setDbStatus('OK');
      } else {
        await db.open();
        setDbStatus('OK');
      }
    } catch (e: any) {
      setDbStatus('ERROR');
      addLog('ERROR', `Fallo crítico en DB: ${e.message}`);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 3. HERRAMIENTAS DE AUTO-REPARACIÓN
  // ─────────────────────────────────────────────────────────────
  
  const fixStuckModals = () => {
    document.body.style.overflow = 'auto';
    document.body.style.paddingRight = '0';
    // Simular escape para cerrar overlays de Radix/HeadlessUI
    const event = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
    document.dispatchEvent(event);
    
    // Forzar limpieza de punteros
    const backdrops = document.querySelectorAll('[data-radix-portal]'); 
    backdrops.forEach(el => el.remove());
    
    toast.success('Interfaz desbloqueada');
  };

  const resetLocalDatabase = async () => {
    if (!confirm('⚠️ PELIGRO: Esto borrará TODOS los datos locales y cerrará sesión. ¿Seguro?')) return;
    try {
      await db.delete();
      localStorage.clear();
      window.location.reload();
    } catch (e) {
      toast.error('No se pudo resetear la DB');
    }
  };

  const clearCacheAndReload = () => {
    localStorage.removeItem('niiz-cart-storage'); // Limpia carrito corrupto
    sessionStorage.clear();
    window.location.reload();
  };

  // --- BOTÓN FLOTANTE (Solo visible si developer_mode es true y no está abierto) ---
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={clsx(
          "fixed bottom-4 left-4 z-[9999] p-3 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 group",
          logs.some(l => l.type === 'ERROR') 
            ? 'bg-red-600 animate-pulse text-white' 
            : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-700'
        )}
        title="Modo Desarrollador Activo"
      >
        <div className="relative">
          <Bug size={24} />
          {logs.some(l => l.type === 'ERROR') && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-slate-900" />
          )}
        </div>
        <span className="absolute left-full ml-2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-700 pointer-events-none">
          System Doctor
        </span>
      </button>
    );
  }

  // --- VENTANA DEL DOCTOR ---
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in zoom-in-95 duration-200">
      <div className="bg-slate-950 w-full max-w-5xl h-[85vh] rounded-xl shadow-2xl border border-slate-800 flex flex-col overflow-hidden text-slate-300 font-mono text-xs md:text-sm">
        
        {/* HEADER */}
        <div className="p-3 border-b border-slate-800 bg-slate-900 flex justify-between items-center select-none">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-indigo-500/20 rounded text-indigo-400">
              <Activity size={18} />
            </div>
            <div>
              <h2 className="font-bold text-white tracking-wide">NIIZ SYSTEM DIAGNOSTICS</h2>
              <div className="flex gap-3 text-[10px] text-slate-500">
                <span>Build: dev-2.5.0</span>
                <span>•</span>
                <span className={isOnline ? "text-emerald-500" : "text-red-500"}>
                  {isOnline ? "NET_ONLINE" : "NET_OFFLINE"}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-800 rounded p-0.5">
              <button 
                onClick={() => setTab('LOGS')}
                className={clsx("px-3 py-1 rounded transition-colors", tab === 'LOGS' ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300")}
              >
                Logs
              </button>
              <button 
                onClick={() => setTab('TOOLS')}
                className={clsx("px-3 py-1 rounded transition-colors", tab === 'TOOLS' ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300")}
              >
                Herramientas
              </button>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-red-900/30 hover:text-red-400 rounded transition-colors">
              <Power size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          
          {/* SIDEBAR: ESTADO VITAL (Siempre visible) */}
          <div className="w-64 bg-slate-900/50 border-r border-slate-800 p-4 space-y-6 overflow-y-auto shrink-0">
            
            <div className="space-y-3">
              <h3 className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Base de Datos</h3>
              <div className="flex items-center justify-between p-2 bg-slate-900 rounded border border-slate-800">
                <span className="flex items-center gap-2"><Database size={14} /> IndexedDB</span>
                <span className={clsx(
                  "text-[10px] font-bold px-1.5 py-0.5 rounded",
                  dbStatus === 'OK' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                )}>
                  {dbStatus}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Estado de Stores</h3>
              
              <div className="p-3 bg-slate-900 rounded border border-slate-800 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Auth:</span>
                  <span className={auth.user ? "text-emerald-400" : "text-amber-400"}>{auth.user ? 'LOGGED' : 'GUEST'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Business:</span>
                  <span className="text-white max-w-[80px] truncate" title={auth.user?.business_id}>{auth.user?.business_id || '-'}</span>
                </div>
              </div>

              <div className="p-3 bg-slate-900 rounded border border-slate-800 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Shift Status:</span>
                  <span className={cash.isShiftOpen ? "text-emerald-400" : "text-slate-400"}>{cash.isShiftOpen ? 'OPEN' : 'CLOSED'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cart Items:</span>
                  <span className="text-white">{cart.items.length}</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800">
              <p className="text-[10px] text-slate-600 text-center">
                Memory Usage: {Math.round((performance as any).memory?.usedJSHeapSize / 1024 / 1024 || 0)} MB
              </p>
            </div>

          </div>

          {/* MAIN CONTENT AREA */}
          <div className="flex-1 flex flex-col bg-slate-950 relative overflow-hidden">
            
            {/* TABS CONTENT */}
            {tab === 'LOGS' ? (
              <>
                <div className="p-2 border-b border-slate-800 bg-slate-900/50 flex justify-between items-center">
                  <div className="flex gap-2 text-slate-500">
                    <Terminal size={14} />
                    <span className="text-[10px]">console_output.log</span>
                  </div>
                  <button onClick={() => setLogs([])} className="text-[10px] hover:text-white transition-colors px-2 py-1 rounded hover:bg-slate-800">
                    Clear Console
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-1 font-mono text-[11px] md:text-xs">
                  {logs.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-700 gap-2">
                      <CheckCircle2 size={32} className="opacity-20" />
                      <p>System operational. No logs.</p>
                    </div>
                  )}
                  {logs.map(log => (
                    <div key={log.id} className="group flex gap-2 hover:bg-slate-900 p-1 rounded transition-colors">
                      <span className="text-slate-600 select-none shrink-0 w-16">{log.timestamp}</span>
                      <span className={clsx(
                        "font-bold shrink-0 w-16",
                        log.type === 'ERROR' ? "text-red-500" :
                        log.type === 'WARNING' ? "text-amber-500" :
                        "text-blue-500"
                      )}>{log.type}</span>
                      <div className="flex-1 min-w-0">
                        <p className={clsx("break-all", log.type === 'ERROR' ? "text-red-200" : "text-slate-300")}>
                          {log.message}
                        </p>
                        {log.stack && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-slate-600 hover:text-slate-400 select-none">[Stack Trace]</summary>
                            <pre className="mt-1 p-2 bg-black rounded text-slate-500 overflow-x-auto border border-slate-800">
                              {log.stack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto">
                {/* TOOL CARD */}
                <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-all group">
                  <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center text-indigo-400 mb-3 group-hover:scale-110 transition-transform">
                    <ShieldAlert size={20} />
                  </div>
                  <h4 className="font-bold text-white mb-1">Desbloqueo UI</h4>
                  <p className="text-slate-500 text-xs mb-4">Fuerza el cierre de modales y restaura el scroll.</p>
                  <button onClick={fixStuckModals} className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-colors">
                    Ejecutar
                  </button>
                </div>

                <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-slate-700 transition-all group">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-400 mb-3 group-hover:scale-110 transition-transform">
                    <RefreshCw size={20} />
                  </div>
                  <h4 className="font-bold text-white mb-1">Limpieza Caché</h4>
                  <p className="text-slate-500 text-xs mb-4">Limpia carritos corruptos y recarga la aplicación.</p>
                  <button onClick={clearCacheAndReload} className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 rounded-lg text-xs font-bold transition-colors">
                    Ejecutar
                  </button>
                </div>

                <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-red-900/50 transition-all group">
                  <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center text-red-400 mb-3 group-hover:scale-110 transition-transform">
                    <Trash2 size={20} />
                  </div>
                  <h4 className="font-bold text-white mb-1">Reset de Fábrica</h4>
                  <p className="text-slate-500 text-xs mb-4">Borra DB local y sesión. Irreversible.</p>
                  <button onClick={resetLocalDatabase} className="w-full py-2 bg-red-950/50 hover:bg-red-900/50 text-red-200 border border-red-900 rounded-lg text-xs font-bold transition-colors">
                    NUKE DATABASE
                  </button>
                </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};